import { CONTINGENT_WORKERS_OPTIONS } from '../definitions/contingentWorkers';
import { DATA_ADJUSTMENTS_OPTIONS } from '../definitions/dataAdjustments';
import { EXTERNALLY_OWNED_OPTIONS } from '../definitions/externallyOwned';
import { FLIGHT_RISK_SELECTIONS } from '../definitions/flightRisk';
import { FUNDING_STAGE_OPTIONS } from '../definitions/fundingStage';
import {
  defaultMetricMode,
  METRIC_MODE_OPTIONS,
} from '../definitions/metricMode';
import { OVERALL_RATING_OPTIONS } from '../definitions/overallRating';
import {
  defaultPostingSource,
  POSTINGS_SOURCE_OPTIONS,
} from '../definitions/postingsSource';
import { PRESTIGE_SELECTIONS } from '../definitions/prestige';
import { PUBLIC_OPTIONS } from '../definitions/public';
import { REMOTE_SUITABILITY_SELECTIONS } from '../definitions/remoteSuitability';
import type { Option } from '../types/filters';
import { type Filter, FilterId, FilterType } from '../types/filters';

const STATIC_FILTER_IDS = [
  FilterId.DATA_ADJUSTMENTS,
  FilterId.POSTINGS_SOURCE,
  FilterId.METRIC_MODE,
  FilterId.OVERALL_RATING,
  FilterId.CONTINGENT_WORKERS,
  FilterId.FLIGHT_RISK,
  FilterId.PRESTIGE,
  FilterId.REMOTE_SUITABILITY,
  FilterId.EXTERNALLY_OWNED,
  FilterId.FUNDING_STAGE,
  FilterId.PUBLIC,
] as const;

export const STATIC_FILTER_DATA: Record<
  (typeof STATIC_FILTER_IDS)[number],
  Option[]
> = {
  [FilterId.DATA_ADJUSTMENTS]: DATA_ADJUSTMENTS_OPTIONS,
  [FilterId.POSTINGS_SOURCE]: POSTINGS_SOURCE_OPTIONS,
  [FilterId.METRIC_MODE]: METRIC_MODE_OPTIONS,
  [FilterId.OVERALL_RATING]: OVERALL_RATING_OPTIONS,
  [FilterId.CONTINGENT_WORKERS]: CONTINGENT_WORKERS_OPTIONS,
  [FilterId.FLIGHT_RISK]: FLIGHT_RISK_SELECTIONS,
  [FilterId.PRESTIGE]: PRESTIGE_SELECTIONS,
  [FilterId.REMOTE_SUITABILITY]: REMOTE_SUITABILITY_SELECTIONS,
  [FilterId.EXTERNALLY_OWNED]: EXTERNALLY_OWNED_OPTIONS,
  [FilterId.FUNDING_STAGE]: FUNDING_STAGE_OPTIONS,
  [FilterId.PUBLIC]: PUBLIC_OPTIONS,
} as const;

/**
 * The options a static list shows. Plain lists get their module constants
 * untouched: react-select keeps the hovered row only while the same option
 * object is still in `options`, so a copy per render would snap the highlight
 * back to the first row on every tick. Metric Mode is the one list that
 * varies, and only its disabled option is rebuilt.
 */
export const getStaticListOptions = ({
  filterId,
  allFilters,
  linkupPostings,
}: {
  filterId: FilterId;
  allFilters: Filter[];
  linkupPostings?: boolean | null;
}): Option[] => {
  const options =
    STATIC_FILTER_DATA[filterId as keyof typeof STATIC_FILTER_DATA];
  if (!options) throw new Error('Invalid filter id');

  switch (filterId) {
    case FilterId.POSTINGS_SOURCE:
      // id 2 is linkup postings, shown only to users with linkup_postings set
      return options.filter((option) => option.value !== '2' || linkupPostings);

    case FilterId.METRIC_MODE: {
      const postingSource = allFilters.find(
        (f) => f.id === FilterId.POSTINGS_SOURCE
      );
      const nonUnifiedSelected =
        postingSource?.type === FilterType.STATIC_LIST &&
        postingSource.selections.some(
          (selection) => selection.id !== defaultPostingSource.value
        );
      if (!nonUnifiedSelected) return options;
      return options.map((option) =>
        option.value === defaultMetricMode.value
          ? option
          : {
              ...option,
              isDisabled: true,
              info: 'Posting Source must be set to Unified (COSMOS) to enable this option.',
            }
      );
    }

    default:
      return options;
  }
};
